import { SiteUrls } from '@tableau/shared-urls';
import { DataGrid } from '@tableau/widgets-datagrid';
import * as _ from 'underscore';

import { SubscriptionGrid } from '../../app/content/SubscriptionGrid';
import { ServerUrls } from '../../app/ServerUrls';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { AngularToReactDataGridBridge } from '../../common/react/components/dataGrid/AngularToReactDataGridBridge';
import { FeatureFlags } from '../FeatureFlags';
import { IScheduleFrequencyDescriptionFilter } from '../filters/ScheduleFrequencyDescriptionFilter';
import { ActionButtonsHelper, IActionsMenuScope, SubplaceWithActionButtonsScope } from '../helpers/ActionButtonsHelper';
import { IActionContext } from '../helpers/ActionContext';
import { CommonErrorCode } from '../helpers/ErrorCodes';
import { SubplaceCountsHelper, SubplaceEvents } from '../helpers/SubplaceCountsHelper';
import { SubscriptionContentType } from '../models/ContentTypes';
import { IEmptyTemplateModel } from '../models/IEmptyTemplate';
import { ISubplace } from '../models/IPlace';
import { ISubscriptionInfo } from '../models/ISubscriptionInfo';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { ResourceQuery } from '../resources/ResourceQuery';
import { IResult } from '../ServerApi/types/IResponse';
import { ActionHelpers } from '../services/ActionHelpers';
import { IActionButton } from '../services/ActionPanelService';
import { BrowserTitleService } from '../services/BrowserTitleService';
import { ChangeScheduleAction } from '../services/ChangeScheduleAction';
import { ChangeSubscriptionDataConditionAction } from '../services/ChangeSubscriptionDataConditionAction';
import { ChangeSubscriptionsFormatAction } from '../services/ChangeSubscriptionsFormatAction';
import { ChangeSubscriptionSubjectAction } from '../services/ChangeSubscriptionSubjectAction';
import { DeleteSubscriptionsAction } from '../services/DeleteSubscriptionsAction';
import { HelpService } from '../services/HelpService';
import { IQueryUpdater, QueryService } from '../services/QueryService';
import { ResumeSubscriptionAction } from '../services/ResumeSubscriptionAction';
import { ServerService } from '../services/ServerService';
import { SorterFactory } from '../services/SorterFactory';
import { SubplaceWithDataGridScope } from './ContentSubplaceCtrl';
import { ITasksScope } from './TasksCtrl';

export interface ISubscriptionsScope
extends ITasksScope,
        IActionsMenuScope<ISubscriptionInfo, {}>,
        SubplaceWithActionButtonsScope<ISubscriptionInfo, {}>,
        SubplaceWithDataGridScope<ISubscriptionInfo> {
}

export class SubscriptionsCtrl {

  public static $inject = [
    '$scope',
    '$translate',
    'AngularToReactDataGridBridge',
    '$state',
    '$filter',
    'BrowserTitleService',
    'subplace',
    'QueryService',
    'SorterFactory',
    'ServerService',
    'HelpService',
    'DeleteSubscriptionsAction',
    'ChangeScheduleAction',
    'ChangeSubscriptionDataConditionAction',
    'ChangeSubscriptionSubjectAction',
    'ResumeSubscriptionAction',
    'ChangeSubscriptionsFormatAction'
  ];

  private query: IQueryUpdater;

  private static emptyTemplateModel: IEmptyTemplateModel = { title: 'subscriptionsEmptyMessage' };

  constructor(
    private $scope: ISubscriptionsScope,
    private $translate: ng.translate.ITranslateService,
    private AngularToReactDataGridBridge: AngularToReactDataGridBridge,
    private $state: ng.ui.IStateService,
    private $filter: ng.IFilterService,
            browserTitleService: BrowserTitleService,
            subplace: ISubplace<ISubscriptionInfo>,
            queryService: QueryService,
            sorterFactory: SorterFactory,
    private ServerService: ServerService,
    private HelpService: HelpService,
    private DeleteSubscriptionsAction: DeleteSubscriptionsAction,
    private changeScheduleAction: ChangeScheduleAction,
    private ChangeSubscriptionDataConditionAction: ChangeSubscriptionDataConditionAction,
    private ChangeSubscriptionSubjectAction: ChangeSubscriptionSubjectAction,
    private ResumeSubscriptionAction: ResumeSubscriptionAction,
    private ChangeSubscriptionsFormatAction: ChangeSubscriptionsFormatAction
  ) {
    browserTitleService.setTitleWithPlaceEntityNameAndSubplaceName({placeEntityName: $scope.objectName, translatableSubplaceName: 'subscriptions'});

    const selectedItems = new Set<ISubscriptionInfo>((subscription: ISubscriptionInfo) => subscription.id);
    $scope.selectedItems = selectedItems;

    const actions = this.createActions(selectedItems);
    const getActionTarget = ActionButtonsHelper.getActionTargetFn($scope, selectedItems);

    this.configureForReactDataGrid(actions, getActionTarget);

    $scope.sorter = sorterFactory.create(SubscriptionContentType.defaultOrder);
    this.query = queryService.createQueryUpdaterFromSubplace(
      $scope,
      subplace,
      (resourceQuery: ResourceQuery<ISubscriptionInfo>) => {

        this.AngularToReactDataGridBridge.updateReactDataGridRowStore({ scope: this.$scope, sliceable: resourceQuery });

        $scope.sliceable = resourceQuery;
        $scope.sorter.setOrder(resourceQuery.getOrder());
      });

    this.refreshSubscriptions();

    $scope.$on(SubplaceEvents.Refresh, () => {
      this.refreshSubscriptions();
    });

    $scope.dataGridProps.updated = (rows: DataGrid.Row[]) => $scope.$apply(() => {
      $scope.selectedItems.updateItems(_.map(_.compact(rows), row => row.data as ISubscriptionInfo));
      SubplaceCountsHelper.updateCountsFromSubplace($scope, subplace, rows.length);
    });

    ActionButtonsHelper.setupScopeForActionsMenuInActionBar({
      scope: $scope,
      isActionAvailable: action => action.isAvailable($scope.selectedItems),
      menuActions: actions,
      getActionTarget,
      translate: this.$translate.instant.bind(this.$translate)
    });
  }

  private configureForReactDataGrid(actions: IActionButton<ISubscriptionInfo, {}>[],
                                    getActionTarget: () => IActionTarget<ISubscriptionInfo, {}>) {
    const columns = SubscriptionGrid.columns({
      translate: this.$translate.instant.bind(this.$translate),
      scope: this.$scope,
      actions,
      getActionTarget,
      isServerPage: this.$state.includes('main.server.**'),
      serverUrls: ServerUrls.getInstance(),
      siteUrls: new SiteUrls({siteUrlName: this.ServerService.sessionSiteUrlName(), queryString: window.location.search}),
      scheduleFrequencyDescriptionFilter: <IScheduleFrequencyDescriptionFilter>this.$filter('scheduleFrequencyDescription'),
      showUserNameColumn: !this.$state.includes('**.user.**'),
      showWorkbookViewColumn: this.$state.includes('**.user.**'),
      showScheduleColumn: !this.$state.includes('**.schedule.**'),
      showSiteColumn: this.$state.includes('main.server.**'),
      suspendedSupportUrl: this.HelpService.getServerHelpUrlByCategory('sub-suspend')
    });

    this.AngularToReactDataGridBridge.attachReactDataGridToScope({
      $scope: this.$scope,
      columns,
      emptyChild: this.AngularToReactDataGridBridge.emptyTemplateElementFor(SubscriptionsCtrl.emptyTemplateModel),
      canShowFilterPanel: SubscriptionContentType.canShowFilterPanel
    });
  }

  private createActions(selectedItems: Set<ISubscriptionInfo>): IActionButton<ISubscriptionInfo, {}>[] {

    const resumeSubscriptionAction: IActionButton<ISubscriptionInfo, {}> = {
      name: 'subscription-resume',
      translate: 'resumeSubscription_action',
      isAvailable: (subscriptions: Set<ISubscriptionInfo>) => subscriptions.size() === 1 && subscriptions.first().suspended,
      execute: (subscriptions: Set<ISubscriptionInfo>, context: IActionContext) => {
        this.ResumeSubscriptionAction.resumeSubscription(subscriptions.first())
          .then((result: IResult) => {
            ActionHelpers.deselectNotFound(selectedItems, result, CommonErrorCode.SubscriptionNotFound);
            this.refreshSubscriptions();
          });
      }
    };

    const changeScheduleAction: IActionButton<ISubscriptionInfo, {}> = {
      name: 'tasks-change-schedule',
      translate: 'changeSchedule_action',
      isAvailable: (subscriptions: Set<ISubscriptionInfo>) => !subscriptions.isEmpty(),
      isDisabled: (subscriptions: Set<ISubscriptionInfo>) =>
        subscriptions.size() === 1 && !subscriptions.first().canManageSubscription,
      execute: (items: Set<ISubscriptionInfo>, context: IActionContext) => {
        this.changeScheduleAction.forSubscriptions(items, context)
          .then((result: IResult) => {
            ActionHelpers.deselectNotFound(selectedItems, result, CommonErrorCode.SubscriptionNotFound);
            this.refreshSubscriptions();
          });
      }
    };

    const changeFrequencyAction: IActionButton<ISubscriptionInfo, {}> = {
      name: 'subscription-change-frequency',
      translate: 'changeSubscriptionsFrequency_action',
      isAvailable: (subscriptions: Set<ISubscriptionInfo>) => !subscriptions.isEmpty(),
      isDisabled: (subscriptions: Set<ISubscriptionInfo>) =>
        subscriptions.size() === 1 && !subscriptions.first().canManageSubscription,
      execute: (items: Set<ISubscriptionInfo>, context: IActionContext) => {
        this.changeScheduleAction.changeSubscriptionsFrequency(items, context)
          .then((result: IResult) => {
            ActionHelpers.deselectNotFound(selectedItems, result, CommonErrorCode.SubscriptionNotFound);
            this.refreshSubscriptions();
          });
      }
    };

    const changeSubjectAction: IActionButton<ISubscriptionInfo, {}> = {
      name: 'tasks-change-subject',
      translate: 'changeSubject_action',
      isAvailable: (subscriptions: Set<ISubscriptionInfo>) => !subscriptions.isEmpty(),
      isDisabled: (subscriptions: Set<ISubscriptionInfo>) =>
        subscriptions.size() !== 1 || !subscriptions.first().canManageSubscription,
      execute: (subscriptions: Set<ISubscriptionInfo>, context: IActionContext) => {
        this.ChangeSubscriptionSubjectAction.changeSubject(subscriptions.first(), context)
          .then((result: IResult) => {
            ActionHelpers.deselectNotFound(selectedItems, result, CommonErrorCode.SubscriptionNotFound);
            this.refreshSubscriptions();
          });
      }
    };

    const changeDataConditionAction: IActionButton<ISubscriptionInfo, {}> = {
      name: 'subscription-change-data-setting',
      translate: 'changeSubscriptionDataCondition_action',
      isAvailable: (subscriptions: Set<ISubscriptionInfo>) => !subscriptions.isEmpty(),
      isDisabled: (subscriptions: Set<ISubscriptionInfo>) =>
          subscriptions.size() !== 1 || !subscriptions.first().canManageSubscription || !subscriptions.first().canSpecifyDataCondition,
      execute: (subscriptions: Set<ISubscriptionInfo>, context: IActionContext) => {
        this.ChangeSubscriptionDataConditionAction.changeDataCondition(subscriptions.first(), context)
          .then((result: IResult) => {
            ActionHelpers.deselectNotFound(selectedItems, result, CommonErrorCode.SubscriptionNotFound);
            this.refreshSubscriptions();
          });
      }
    };

    const unsubscribeAction: IActionButton<ISubscriptionInfo, {}> = {
      name: 'unsubscribe-subscriptions',
      translate: 'unsubscribe_action',
      isAvailable: (subscriptions: Set<ISubscriptionInfo>) => !subscriptions.isEmpty(),
      isDisabled: (subscriptions: Set<ISubscriptionInfo>) =>
        subscriptions.size() === 1 && !subscriptions.first().canManageSubscription,
      execute: (subscriptions: Set<ISubscriptionInfo>, context: IActionContext) => {
        this.DeleteSubscriptionsAction.deleteSubscriptions(subscriptions, context)
          .then((result: IResult) => {
            ActionHelpers.deselectAllExceptErrors(selectedItems, result, CommonErrorCode.SubscriptionNotFound);
            this.refreshSubscriptions();
          });
      }
    };

    const changeFormatAction: IActionButton<ISubscriptionInfo, {}> = {
      name: 'subscription-change-format',
      translate: 'changeSubscriptionsFormat_action',
      isAvailable: (subscriptions: Set<ISubscriptionInfo>) => !subscriptions.isEmpty() &&
          FeatureFlags.pdfsubscriptionsSP1Enabled && (FeatureFlags.pdfsubscriptionsUIEnabled ||
            (!subscriptions.isEmpty() && subscriptions.all(subscription => subscription.attachPdf))),
      execute: (items: Set<ISubscriptionInfo>, context: IActionContext) => {
        this.ChangeSubscriptionsFormatAction.changeFormat(items, context)
          .then((result: IResult) => {
            ActionHelpers.deselectNotFound(selectedItems, result, CommonErrorCode.SubscriptionNotFound);
            this.refreshSubscriptions();
          });
      }
    };

    return _.compact([
      FeatureFlags.suspendSubscriptions ? resumeSubscriptionAction : false, // compact automatically removes falsy values
      FeatureFlags.subscriptionsOnExtractRefreshEnabled ? changeFrequencyAction : changeScheduleAction,
      changeSubjectAction,
      changeDataConditionAction,
      // Don't show change format if UI FF is disabled and non-PDF subscription is selected.
      FeatureFlags.pdfsubscriptionsSP1Enabled && (FeatureFlags.pdfsubscriptionsUIEnabled ||
        (!selectedItems.isEmpty() && selectedItems.all(subscription => subscription.attachPdf))) ? changeFormatAction : false,
      unsubscribeAction
    ]);
  }

  private refreshSubscriptions() {
    this.query.refresh();
  }

}

Module.instance().controller('SubscriptionsCtrl', SubscriptionsCtrl);
